import { useProjectsQuery } from 'client/state/queries/projects.query'
import { useDownlinkStore } from 'client/state/stores/downlink'
import type {
  DownlinkProjectConfig,
  DownlinkState,
  DownlinkWorkerInterface
} from 'client/workers/downlink/defs'
import { proxy, wrap } from 'comlink'
import { RFC7807Error } from 'modules/errors/rfc7807'
import { isServer } from 'modules/std/ssr'
import React from 'react'

export const DownlinkController: React.FC = () => {
  // This component runs only on the client,
  // and is used to avoid calling the useDownlink hook
  // in SSR where it makes no sense.
  useDownlinkController()
  return null
}

export const downlinkWorker = wrap<DownlinkWorkerInterface>(
  new Worker(
    new URL(
      /* webpackChunkName: "downlink.worker" */
      'client/workers/downlink/main',
      import.meta.url
    ),
    {
      name: 'downlink'
    }
  )
)

/**
 * This hook deals with:
 * - Setting up the worker with the list of projects to pull
 * - Starting & stopping when going online/offline
 */
function useDownlinkController() {
  if (isServer) {
    throw new Error('No SSR!')
  }

  const projects = useProjectsQuery()
  // const isOnline = useOnlineStatus()
  // const [onlineDetectionEnabled, setOnlineDetectionEnabled] =
  //   React.useState(false)

  /**
   * Update downlink worker with the current list of projects
   */
  React.useEffect(() => {
    const projectConfigs = projects.data
      ? projects.data.map<DownlinkProjectConfig>(project => ({
          id: project.id,
          secretKey: project.secretKey
        }))
      : []
    if (projectConfigs.length === 0) {
      return
    }

    downlinkWorker.setup(
      proxy({
        state: state => {
          useDownlinkStore.setState(state)
        },
        error: downlinkError => {
          const error = new RFC7807Error({
            type: 'https://downlink-error',
            title: downlinkError.name,
            detail: downlinkError.message
          })
          // todo: Show toast without re-running useEffect
          console.error(error)
        }
      })
    )

    console.debug(
      `Starting downlink worker with ${projectConfigs.length} project(s)`
    )
    // Disable online detection while updating projects
    // setOnlineDetectionEnabled(false)

    downlinkWorker
      .start(projectConfigs)
      // .then(() => {
      //   // todo: This will not resolve since the stream is continuous
      //   setOnlineDetectionEnabled(true)
      // })
      .catch(console.error)
  }, [projects.data])

  /**
   * Disable downlink when going offline
   * and re-enable when coming back online.
   */
  // React.useEffect(() => {
  //   if (!onlineDetectionEnabled) {
  //     return
  //   }
  //   if (isOnline) {
  //     downlinkWorker.isRunning.then(isRunning => {
  //       if (!isRunning) {
  //         console.debug('Back online: starting downlink worker')
  //         downlinkWorker.start()
  //       }
  //     })
  //   } else {
  //     console.debug('Offline: stopping downlink worker')
  //     downlinkWorker.stop()
  //   }
  // }, [isOnline, onlineDetectionEnabled])
}

function isDownlinkRunning(state: DownlinkState) {
  return state.status === 'catchingUp' || state.status === 'live'
}

export function useDownlinkRunningStatus() {
  return useDownlinkStore(isDownlinkRunning)
}
